import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import { analyticsControllerGetOrdersDrilldownCsv } from '@/generated/korza-api-client';
import {
  getCsvErrorToken,
  getDrilldownCsvFileName,
  type DrilldownOrigin,
  type DrilldownQuery,
} from '@/lib/orders-drilldown';
import { toast } from '@/components/ui/sonner';

/** Backend ceiling for synchronous CSV exports. */
const CSV_ROW_LIMIT = 50_000;

interface UseOrdersDrilldownCsvInput {
  tenantSlug: string;
  query: DrilldownQuery | null;
  origin: DrilldownOrigin | null;
}

interface OrdersDrilldownCsv {
  download(): Promise<void>;
  isLoading: boolean;
  /** True for a moment after a successful download. */
  isDone: boolean;
}

/** CSV export of the drill-down list (same request in the dialog and the sheet). */
export function useOrdersDrilldownCsv({
  tenantSlug,
  query,
  origin,
}: UseOrdersDrilldownCsvInput): OrdersDrilldownCsv {
  const { LL, locale } = useI18nContext();
  const apiLocale = locale === 'pt' ? 'pt' : 'en';

  const isCsvRunning = useRef(false);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [isCsvDone, setIsCsvDone] = useState(false);

  const downloadCsv = async () => {
    if (query === null || isCsvRunning.current) return;
    isCsvRunning.current = true;
    setIsCsvLoading(true);
    try {
      const response = await analyticsControllerGetOrdersDrilldownCsv({
        path: { tenantSlug },
        query: {
          fromDate: query.fromDate,
          toDate: query.toDate,
          source: query.source,
          bucketStart: query.bucketStart,
          granularity: query.granularity,
          view: query.view,
          search: query.search,
          sort: query.sort,
          locale: apiLocale,
        },
        parseAs: 'blob',
        throwOnError: true,
      });

      const blob = response.data as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      if (origin !== null) {
        link.download = getDrilldownCsvFileName(
          origin,
          locale,
          tenantSlug,
          DateTime.now()
            .startOf('second')
            .toISO({ includeOffset: false, suppressMilliseconds: true })
        );
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setIsCsvDone(true);
      window.setTimeout(() => setIsCsvDone(false), 2500);
    } catch (error) {
      const token = getCsvErrorToken(error);
      toast.error(
        token === 'metricsPage.repurchaseDrilldown.csvTooLarge'
          ? LL.metricsPage.repurchaseDrilldown.csvTooLarge({
              limit: new Intl.NumberFormat(locale).format(CSV_ROW_LIMIT),
            })
          : LL.metricsPage.repurchaseDrilldown.csvError()
      );
    } finally {
      isCsvRunning.current = false;
      setIsCsvLoading(false);
    }
  };

  return { download: downloadCsv, isLoading: isCsvLoading, isDone: isCsvDone };
}
